import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, HeartHandshake } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProgramBadge from "@/components/sponsors/ProgramBadge";
import { Spinner } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/safety/Dialogs";
import PastDueBanner from "@/components/dashboard/PastDueBanner";
import DashboardStats from "@/components/dashboard/DashboardStats";
import ConnectionsSection from "@/components/dashboard/ConnectionsSection";
import MessagesPreview from "@/components/dashboard/MessagesPreview";
import RecommendedSponsors from "@/components/dashboard/RecommendedSponsors";
import SubscriptionCard from "@/components/dashboard/SubscriptionCard";
import FavoritesCard from "@/components/dashboard/FavoritesCard";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [convos, setConvos] = useState({ conversations: [], total_unread: 0 });
  const [recommended, setRecommended] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [billing, setBilling] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, o, m, r, f, b] = await Promise.all([
        api.get("/connections"),
        api.get("/requests/outgoing"),
        api.get("/conversations"),
        api.get("/sponsors/recommended"),
        api.get("/favorites"),
        api.get("/billing/me"),
      ]);
      setConnections(c.data.connections);
      setOutgoing(o.data.requests);
      setConvos(m.data);
      setRecommended(r.data.sponsors);
      setFavorites(f.data.favorites);
      setBilling(b.data.subscription);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openPortal = useCallback(async () => {
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  const resumeSubscription = useCallback(async () => {
    try {
      await api.post("/billing/resume");
      await refresh();
      await load();
      toast.success("Your membership will keep renewing.");
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [refresh, load]);

  if (loading) return <Spinner label="Loading your dashboard" />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#1F2A2E]">Welcome Back</h1>
          <p className="mt-1.5 text-[#4B5A60]">
            {user.display_name}
            {user.recovery_program ? (
              <span className="ml-2 inline-block align-middle">
                <ProgramBadge program={user.recovery_program} />
              </span>
            ) : null}
          </p>
        </div>
        {user.account_type === "sponsor" && (
          <Button
            onClick={() => navigate("/sponsor-dashboard")}
            data-testid="go-sponsor-dashboard"
            className="bg-[#1F6F78] hover:bg-[#185B62] text-white"
          >
            <HeartHandshake className="w-4 h-4 mr-2" /> Sponsor Dashboard
          </Button>
        )}
      </div>

      {billing?.subscription_status === "past_due" && (
        <PastDueBanner
          onUpdatePayment={openPortal}
          onSeeFreeOptions={() => navigate("/pricing#access-pass")}
        />
      )}

      {/* Quick stats */}
      <DashboardStats
        user={user}
        connections={connections}
        convos={convos}
        favorites={favorites}
      />

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 min-w-0 space-y-6">
          {/* Connections */}
          <ConnectionsSection
            connections={connections}
            outgoing={outgoing}
            navigate={navigate}
          />

          {/* Messages */}
          <MessagesPreview convos={convos} />

          {/* Recommended */}
          <RecommendedSponsors recommended={recommended} navigate={navigate} />
        </div>

        {/* Right rail */}
        <div className="min-w-0 space-y-6">
          <SubscriptionCard
            user={user}
            billing={billing}
            navigate={navigate}
            onOpenPortal={openPortal}
            onResume={resumeSubscription}
            onRequestCancel={() => setCancelOpen(true)}
          />

          <FavoritesCard favorites={favorites} />

          <section className="rounded-2xl bg-[#1F2A2E] text-[#F7F4EF] p-6">
            <Bell className="w-5 h-5 text-[#D07A4A]" />
            <h2 className="mt-2 font-display text-lg font-bold">Stay in the loop</h2>
            <p className="mt-1.5 text-sm text-[#CBD6D8] leading-6">
              We notify you when a sponsor accepts your request, when you get a new message, and
              when a favorited sponsor opens up availability.
            </p>
            <Link
              to="/notifications"
              className="mt-3 inline-block text-sm font-semibold underline text-white"
            >
              View notifications
            </Link>
          </section>
        </div>
      </div>

      <ConfirmDialog
        open={cancelOpen}
        danger
        title="Cancel your membership?"
        description="Your membership stays active until the end of the paid period, and you keep unlimited messaging until then. You can resume auto-renewal at any time."
        confirmLabel="Cancel membership"
        onClose={() => setCancelOpen(false)}
        onConfirm={async () => {
          try {
            const { data } = await api.post("/billing/cancel");
            toast.success(data.message);
            setCancelOpen(false);
            await refresh();
            await load();
          } catch (e) {
            toast.error(apiError(e));
          }
        }}
      />
    </div>
  );
}
